import { createOperationDescriptor, getRequest, fetchQuery, graphql } from 'relay-runtime';
import { v4 as uuid } from 'uuid';
import { fg } from '@atlassian/jira-feature-gating';
import { isCustomFilter } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { getOperationScope } from '@atlassian/jira-issue-table-common/src/utils/index.tsx';
import type { actions_issueSearchTotalCountQuery } from '@atlassian/jira-relay/src/__generated__/actions_issueSearchTotalCountQuery.graphql';
import RefetchQuery, {
	type IssueNavigatorIssueSearchRefetchQuery,
	type IssueNavigatorIssueSearchRefetchQuery$variables as IssueNavigatorQueryVariables,
	type JiraIssueSearchOperationScope,
	type JiraIssueSearchScope,
} from '@atlassian/jira-relay/src/__generated__/IssueNavigatorIssueSearchRefetchQuery.graphql';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { createViewAri } from '@atlassian/jira-issue-navigator-view-ari/src/index.tsx';

import {
	DEFAULT_VIEW_ID,
	MAX_AMOUNT_OF_COLUMNS,
	MAX_ISSUES_PER_PAGE,
	NIN_PROJECT_OPERATION_SCOPE,
	NIN_GLOBAL_OPERATION_SCOPE,
} from '../../common/constants.tsx';
import {
	convertFilterIdToIssueNavigatorId,
	isFilterViewId,
	parseIssueNavigatorViewIdOrDefault,
} from '../../common/utils/index.tsx';
import { fetchIssueNavigatorIssueSearchRefetchQueryShadow } from '../issue-search-shadow-request/index.tsx';
import type { Actions, Props, State } from './types.tsx';

/**
 * Private action to refetch issue search results when input arguments have updated.
 */
export const handleSearchInputUpdate =
	(): Action<State, Props> =>
	({ dispatch }, { issueSearchInput, filterId, viewId }) => {
		const shouldLoadWithFilterViewId =
			viewId !== 'detail' && issueSearchInput.filterId && isCustomFilter(issueSearchInput.filterId);
		const isCurrentViewIdInvalid = isFilterViewId(viewId) && viewId !== `list_filter_${filterId}`;

		let variables = {};
		if (issueSearchInput.filterId && shouldLoadWithFilterViewId) {
			const issueNavigatorViewId = convertFilterIdToIssueNavigatorId(issueSearchInput.filterId);
			variables = {
				viewId: issueNavigatorViewId,
				fieldSetsInput: {
					viewInput: {
						namespace: 'ISSUE_NAVIGATOR',
						viewId: issueNavigatorViewId,
					},
				},
			};
		} else if (isCurrentViewIdInvalid) {
			variables = {
				viewId: DEFAULT_VIEW_ID,
				fieldSetsInput: {
					viewInput: {
						namespace: 'ISSUE_NAVIGATOR',
						viewId: DEFAULT_VIEW_ID,
					},
				},
			};
		}

		dispatch(actions.refetch(variables));
	};

/**
 * Private action to reset the uncapped total issue count.
 */
export const onResetTotalIssueCount =
	(): Action<State, Props> =>
	({ setState }) => {
		setState({
			totalIssueCount: null,
			totalIssueCountIsFetching: false,
			totalIssueCountIsError: false,
		});
	};

export const actions: Actions = {
	changeView:
		(viewId) =>
		(
			{ dispatch },
			{
				onSelectedViewMutation,
				isIssueHierarchySupportEnabled,
				activationId,
				cloudId,
				projectId,
				viewItemId,
				issueSearchInput,
			},
		) => {
			let viewARI = '';
			if (fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')) {
				const layout = viewId === 'detail' ? 'detail' : 'list';

				viewARI = createViewAri({
					activationId,
					cloudId,
					layout,
					projectId,
					viewItemId,
				});
			}
			onSelectedViewMutation(viewId, viewARI);
			dispatch(
				actions.refetch({
					viewId,
					fieldSetsInput: {
						viewInput: {
							namespace: 'ISSUE_NAVIGATOR',
							viewId,
						},
					},

					...((isIssueHierarchySupportEnabled || fg('jsc_hide_done_view_config_fix')) && {
						viewConfigInput: { viewInput: { namespace: 'ISSUE_NAVIGATOR', viewId } },
						shouldQueryHasChildren: isIssueHierarchySupportEnabled ? viewId !== 'detail' : false,
						staticViewInput: null,
					}),
					...(fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')
						? {
								settings: {
									jql: issueSearchInput.jql,
								},
							}
						: {}),
				}),
			);
		},
	resetView:
		() =>
		({ dispatch }, { activationId, cloudId, projectId, viewItemId }) => {
			dispatch(
				actions.refetch({
					fieldSetsInput: {
						viewInput: {
							namespace: 'ISSUE_NAVIGATOR',
						},
					},
					settings: null,
					staticViewInput: null,
					viewConfigInput: {
						staticViewInput: {
							isGroupingEnabled: null,
							isHideDoneEnabled: null,
							isHierarchyEnabled: null,
						},
					},
					viewQueryInput: {
						viewAri: createViewAri({
							activationId,
							cloudId,
							projectId,
							viewItemId,
						}),
					},
				}),
			);
		},
	changeHierarchy:
		(nextIsHierarchyEnabled) =>
		({ dispatch }, { isHierarchyEnabled, groupedByField, isHideDoneEnabled, issueSearchInput }) => {
			if (nextIsHierarchyEnabled !== isHierarchyEnabled) {
				const staticViewInput = {
					isHierarchyEnabled: nextIsHierarchyEnabled,
					isGroupingEnabled: !!groupedByField,
					...(fg('jsc_list_hide_done_items') ? { isHideDoneEnabled } : {}),
				};
				const settings = {
					hideDone: isHideDoneEnabled,
					hierarchyEnabled: nextIsHierarchyEnabled,
					groupBy: groupedByField,
					jql: issueSearchInput.jql,
				};
				dispatch(
					actions.refetch({
						staticViewInput,
						viewConfigInput: { staticViewInput },
						shouldQueryHasChildren: Boolean(nextIsHierarchyEnabled),
						...(fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')
							? { settings }
							: {}),
					}),
				);
			}
		},
	changeGroupedByField:
		(nextGroupedByField) =>
		({ dispatch }, { isHierarchyEnabled, isHideDoneEnabled, issueSearchInput }) => {
			const staticViewInput = {
				isHierarchyEnabled,
				isGroupingEnabled: !!nextGroupedByField,
				...(fg('jsc_list_hide_done_items') ? { isHideDoneEnabled } : {}),
			};
			const settings = {
				hideDone: isHideDoneEnabled,
				hierarchyEnabled: isHierarchyEnabled,
				groupBy: nextGroupedByField ?? null,
				jql: issueSearchInput.jql,
			};
			dispatch(
				actions.refetch({
					groupBy: nextGroupedByField,
					staticViewInput,
					viewConfigInput: { staticViewInput },
					...(fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')
						? { settings }
						: {}),
				}),
			);
		},
	// Remove action when cleaning up jira_list_nin_merge_onboarding_modal
	changeGroupedByFieldAndJql:
		(nextGroupedByField, nextJql) =>
		({ dispatch }, { isHierarchyEnabled, isHideDoneEnabled, issueSearchInput }) => {
			const staticViewInput = {
				isHierarchyEnabled,
				isGroupingEnabled: !!nextGroupedByField,
				...(fg('jsc_list_hide_done_items') ? { isHideDoneEnabled } : {}),
			};
			const settings = {
				hideDone: isHideDoneEnabled,
				hierarchyEnabled: isHierarchyEnabled,
				groupBy: nextGroupedByField ?? null,
				jql: nextJql ?? issueSearchInput.jql,
			};

			dispatch(
				actions.refetch({
					groupBy: nextGroupedByField,
					staticViewInput,
					viewConfigInput: { staticViewInput },
					...(fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')
						? { settings }
						: {}),
					...(nextJql
						? {
								issueSearchInput: {
									jql: nextJql,
								},
							}
						: {}),
				}),
			);
		},
	changeHideDone:
		(nextIsHideDoneEnabled) =>
		({ dispatch }, { isHierarchyEnabled, groupedByField, isHideDoneEnabled, issueSearchInput }) => {
			if (nextIsHideDoneEnabled !== isHideDoneEnabled) {
				const staticViewInput = {
					isHierarchyEnabled,
					isGroupingEnabled: !!groupedByField,
					isHideDoneEnabled: nextIsHideDoneEnabled,
				};
				const settings = {
					hideDone: nextIsHideDoneEnabled,
					hierarchyEnabled: isHierarchyEnabled,
					groupBy: groupedByField,
					jql: issueSearchInput.jql,
				};
				dispatch(
					actions.refetch({
						staticViewInput,
						viewConfigInput: { staticViewInput },
						shouldQueryHasChildren: Boolean(isHierarchyEnabled),
						...(fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')
							? { settings }
							: {}),
					}),
				);
			}
		},
	refetch:
		(variables = {}, options = {}) =>
		(
			{ dispatch, getState, setState },
			{
				cloudId,
				environment,
				issueSearchInput,
				onRefetchStart,
				onRefetchError,
				refetch,
				viewId,
				isHierarchyEnabled,
				isConditionalFormattingEnabled,
				isSavedViewConditionalFormattingEnabled,
				isInlineFieldConfigEnabled,
				projectContext,
				groupedByField,
				isHideDoneEnabled,
				activationId,
				projectId,
				viewItemId,
			},
		) => {
			let { inFlightRequest } = getState();

			// Some consumers may fire refetch queries before in-flight query
			// has been completed (e.g. while user is toggling columns on and off in
			// the column picker). In these situations, we always want to fetch latest
			// data from the API as column configuration may have changed for the user.
			// To do that, we disable Relay's automatic de-deduping of requests.
			inFlightRequest?.unsubscribe();

			const staticViewInput = {
				isHierarchyEnabled,
				isGroupingEnabled: !!groupedByField,
				isHideDoneEnabled,
			};

			const settings = {
				hideDone: isHideDoneEnabled,
				hierarchyEnabled: isHierarchyEnabled,
				groupBy: groupedByField,
				jql: issueSearchInput.jql,
			};

			let operationScope: JiraIssueSearchOperationScope;

			if (isInlineFieldConfigEnabled) {
				operationScope = getOperationScope(projectContext);
			} else if (projectContext) {
				operationScope = NIN_PROJECT_OPERATION_SCOPE;
			} else {
				operationScope = NIN_GLOBAL_OPERATION_SCOPE;
			}

			const scope: JiraIssueSearchScope = {
				operationScope,
			};
			// Behavioural change from IFC. Previously we omitted the project key in embed view - which
			// means default global field sets would be loaded. Now we load field sets configured to the
			// project in both project and embed routes
			if (fg('jira_nin_last_used_jql')) {
				scope.projectKey = projectContext ?? null;
			} else if (projectContext && isInlineFieldConfigEnabled) {
				scope.projectKey = projectContext ?? null;
			}

			let fieldSetsScope = null;
			if (fg('jira_nin_last_used_jql')) {
				fieldSetsScope = {
					operationScope,
					projectKey: projectContext ?? null,
				};
			} else if (isInlineFieldConfigEnabled) {
				fieldSetsScope = {
					operationScope,
					projectKey: projectContext,
				};
			}

			const finalVariables: IssueNavigatorQueryVariables = {
				cloudId,
				issueSearchInput,
				first: MAX_ISSUES_PER_PAGE,
				after: null,
				namespace: 'ISSUE_NAVIGATOR',
				viewId,
				options: null,
				shouldQueryHasChildren: isHierarchyEnabled || false,
				fieldSetsInput: {
					viewInput: {
						namespace: 'ISSUE_NAVIGATOR',
						viewId,
					},
				},
				amountOfColumns: MAX_AMOUNT_OF_COLUMNS,
				...(fg('jsc_nin_operation_scope_fe') && {
					scope,
				}),
				filterId: null,
				staticViewInput,
				viewConfigInput: { staticViewInput },
				...(fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support')
					? { settings }
					: {}),
				groupBy: groupedByField,
				isPaginating: false,
				projectKeys: projectContext ? [projectContext] : null,
				projectKey: projectContext ?? null,
				isConditionalFormattingEnabled,
				isSavedViewConditionalFormattingEnabled,
				fieldSetsScope,
				...(fg('jira_nin_last_used_jql')
					? { saveJQLToUserHistory: true, isLastUsedJqlEnabled: true }
					: { saveJQLToUserHistory: false, isLastUsedJqlEnabled: false }),
				...variables,
			};

			if (
				finalVariables.viewQueryInput == null &&
				(fg('saved_list_view_technical_changes') || fg('saved_list_view_merge_support'))
			) {
				let layout: 'grouped' | 'detail' | 'list' = 'list';

				if (finalVariables.viewId === 'detail') {
					layout = 'detail';
				} else if (finalVariables.groupBy != null) {
					layout = 'grouped';
				}

				finalVariables.viewQueryInput = {
					viewAri: createViewAri({
						activationId,
						cloudId,
						layout,
						projectId,
						viewItemId,
					}),
				};
			}

			inFlightRequest = fetchQuery<IssueNavigatorIssueSearchRefetchQuery>(
				environment,
				RefetchQuery,
				finalVariables,
				{
					fetchPolicy: 'network-only',
					networkCacheConfig: {
						metadata: { META_SLOW_ENDPOINT: true },
					},
				},
			).subscribe({
				complete: () => {
					/**
					 * fetchQuery will NOT retain the data for the query, meaning that it is not guaranteed that the
					 * data will remain saved in the Relay store at any point after the request completes.
					 * We need to explicitly retain the operation to ensure it doesn't get deleted.
					 * See https://relay.dev/docs/api-reference/fetch-query/#behavior
					 */
					const operation = createOperationDescriptor(getRequest(RefetchQuery), finalVariables);
					const { disposables } = getState();
					setState({
						disposables: disposables.concat([environment.retain(operation)]),
					});

					let hasStoreRefetchCompleted = false;

					const storeRefetch = () => {
						refetch(finalVariables, {
							fetchPolicy: 'store-only',
							onComplete: () => {
								/**
								 * Relay will dispose the cached operation after a 5m timeout, in which case it will
								 * refetch the query from the store and retrigger the onComplete callback. We need to
								 * ensure this callback is invoked strictly once to prevent unexpected side effects,
								 * e.g. https://jira.atlassian.com/browse/JRACLOUD-84106.
								 */
								if (hasStoreRefetchCompleted) {
									return;
								}

								hasStoreRefetchCompleted = true;

								/**
								 * A very difficult to debug race condition emerges when setState is called in the same
								 * update cycle as our refetch function. It appears to be related to useSyncExternalStore
								 * (used internally by RSS) internal state becoming out of sync which prevents it from
								 * correctly notifying listeners (i.e. hooks consumers) when an update is made. Scheduling
								 * the update at the end of the event loop is the simplest way of mitigating the bug,
								 * but we should revisit this once React concurrent rendering is enabled.
								 *
								 * Bug this fixes: When clicking "refresh" button multiple times, the shaded loading
								 * state on the table will only appear the first time.
								 */
								queueMicrotask(() => {
									setState({
										isFetching: false,
										isFetchingView: false,
										searchKey: uuid(),
										...(fg('jira_list_nin_merge_technical_changes') && {
											inFlightIssueSearchInput: null,
										}),
									});
									dispatch(onResetTotalIssueCount());

									options.onComplete?.();
								});
							},
						});
					};

					/**
					 * Delaying the store refetch until the next tick in order to avoid a race condition that occurs
					 * when react concurrent mode is enabled. In the future (once concurrent mode rollout is
					 * complete), we should replace this suspenseless refetch pattern with transitions.
					 *
					 * Bug this fixes: Features that perform state updates shortly after the refetch (like toggling
					 * hierarchy or grouping) can result in a flicker between old results an new results.
					 */
					setTimeout(storeRefetch, 0);

					if (fg('saved_list_view_shadow_request')) {
						fetchIssueNavigatorIssueSearchRefetchQueryShadow(finalVariables);
					}
				},
				error: (error: Error) => {
					setState({
						isFetching: false,
						isFetchingView: false,
						isNetworkError: true,
						...(fg('jira_list_nin_merge_technical_changes') && { inFlightIssueSearchInput: null }),
					});
					options.onError?.();
					onRefetchError?.(error, parseIssueNavigatorViewIdOrDefault(finalVariables.viewId));
				},
				unsubscribe: () => {
					options.onUnsubscribe?.();
				},
			});

			onRefetchStart?.();
			setState({
				inFlightRequest,
				isFetching: true,
				isFetchingView: viewId !== finalVariables.viewId,
				isNetworkError: false,
				...(fg('jira_list_nin_merge_technical_changes') && {
					inFlightIssueSearchInput: finalVariables.issueSearchInput,
				}),
			});
		},
	refresh:
		() =>
		({ setState, dispatch }) => {
			setState({ isRefreshing: true });
			const onFinish = () => {
				setState({ isRefreshing: false });
			};
			dispatch(
				actions.refetch(
					{},
					{
						onComplete: onFinish,
						onError: onFinish,
						onUnsubscribe: onFinish,
					},
				),
			);
		},
	fetchTotalIssueCount:
		() =>
		(
			{ setState },
			{
				environment,
				cloudId,
				issueSearchInput,
				isHierarchyEnabled,
				isHideDoneEnabled,
				isIssueHierarchySupportEnabled,
			},
		) => {
			setState({
				totalIssueCount: null,
				totalIssueCountIsFetching: true,
				totalIssueCountIsError: false,
			});
			const viewConfigInput =
				isIssueHierarchySupportEnabled || (isHideDoneEnabled && fg('jsc_list_hide_done_items'))
					? { staticViewInput: { isHierarchyEnabled, isHideDoneEnabled } }
					: null;

			fetchQuery<actions_issueSearchTotalCountQuery>(
				environment,
				// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
				graphql`
					query actions_issueSearchTotalCountQuery(
						$cloudId: ID!
						$issueSearchInput: JiraIssueSearchInput!
						$viewConfigInput: JiraIssueSearchViewConfigInput
					) {
						jira {
							issueSearchTotalCount(
								cloudId: $cloudId
								issueSearchInput: $issueSearchInput
								viewConfigInput: $viewConfigInput
							) @optIn(to: ["JiraTotalIssueCount"])
						}
					}
				`,
				{ cloudId, issueSearchInput, viewConfigInput },
				{
					fetchPolicy: 'network-only',
					networkCacheConfig: {
						metadata: { META_SLOW_ENDPOINT: true },
					},
				},
			).subscribe({
				next: (data) => {
					const totalIssueCount = data?.jira?.issueSearchTotalCount ?? null;
					const totalIssueCountIsError = totalIssueCount === null;

					setState({
						totalIssueCount,
						totalIssueCountIsFetching: false,
						totalIssueCountIsError,
					});
				},
				error: () => {
					setState({
						totalIssueCount: null,
						totalIssueCountIsFetching: false,
						totalIssueCountIsError: true,
					});
				},
			});
		},
};
